import { useState } from "react";
import { toast } from "sonner";
import { Clock, MoreVertical, History, Plus, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProofForm } from "@/components/Proof";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tag } from "@/components/kit";
import { TaskDialog } from "@/components/TaskDialog";
import { ConfirmDialog, NoteDialog, HistoryDialog, DIALOG_CLS } from "@/components/Dialogs";
import { useStore } from "@/store/AppStore";
import { STATUS, FREQ, isOverdue, assigneeLabel, sortTasks } from "@/lib/tasks";
import { teamOf, nameMap } from "@/lib/selectors";
import { fmtDate } from "@/lib/format";

const PRIO = { Alta: "dark", Média: "gold", Baixa: "muted" };
const FILTERS = [["todas", "Todas"], ["pendente", "Pendentes"], ["andamento", "Em andamento"], ["aguardando", "Aguardando"], ["concluida", "Concluídas"], ["atrasadas", "Atrasadas"]];
const match = (t, f) => f === "todas" || (f === "atrasadas" ? isOverdue(t) : t.status === f);

function StatusControls({ t, mode, actor, open }) {
  const { setTaskStatus, reviewTask } = useStore();
  const btn = "h-8 px-3 text-xs";
  if (t.status === "aguardando") {
    if (mode !== "owner") return <p className="text-xs text-neutral-500">Aguardando aprovação da empresária</p>;
    return (
      <div className="flex gap-2">
        <Button size="sm" className={btn} data-testid={`task-approve-${t.id}`} onClick={() => { reviewTask(t.id, true, actor); toast.success("Tarefa aprovada."); }}>Aprovar</Button>
        <Button size="sm" variant="outline" className={btn} data-testid={`task-reject-${t.id}`} onClick={() => open("reject")}>Devolver</Button>
      </div>
    );
  }
  if (t.status === "concluida") return <Button size="sm" variant="outline" className={btn} data-testid={`task-reopen-${t.id}`} onClick={() => open("reopen")}>Reabrir / corrigir</Button>;
  const set = (s) => {
    if (s === "concluida" && t.kind === "operacional") return open("proof");
    setTaskStatus(t.id, s, actor);
    if (s === "concluida") toast.success(t.requiresApproval ? "Enviada para aprovação." : "Tarefa concluída.");
  };
  return (
    <div className="flex flex-wrap gap-2">
      {[["pendente", "Pendente"], ["andamento", "Em andamento"]].map(([s, l]) => (
        <button key={s} onClick={() => set(s)} data-testid={`task-set-${s}-${t.id}`} className={`h-8 rounded-md border px-3 text-xs font-medium transition-colors ${t.status === s ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600 hover:border-gold"}`}>{l}</button>
      ))}
      <Button size="sm" className={`${btn} font-semibold`} data-testid={`task-set-concluida-${t.id}`} onClick={() => set("concluida")}>Concluir</Button>
    </div>
  );
}

function TaskMenu({ t, mode, open }) {
  if (mode !== "owner") return <Button variant="ghost" size="icon" aria-label="Histórico" data-testid={`task-history-${t.id}`} onClick={() => open("history")}><History className="h-4 w-4 text-neutral-500" /></Button>;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Ações" data-testid={`task-menu-${t.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid={`task-edit-${t.id}`} onSelect={() => open("edit")}>Editar</DropdownMenuItem>
        <DropdownMenuItem data-testid={`task-history-${t.id}`} onSelect={() => open("history")}>Histórico</DropdownMenuItem>
        <DropdownMenuItem data-testid={`task-delete-${t.id}`} className="text-red-600" onSelect={() => open("delete")}>Excluir</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TaskRow({ t, mode, actor, names, people, processes }) {
  const { saveTask, deleteTask, reviewTask, reopenTask, completeWithProof } = useStore();
  const [dlg, setDlg] = useState(null);
  const close = (v) => !v && setDlg(null);
  const proc = processes.find((p) => p.id === t.processId);
  const st = STATUS[t.status];
  return (
    <li data-testid={`task-${t.id}`} className="py-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${t.status === "concluida" ? "text-neutral-400 line-through" : "text-neutral-900"}`}>{t.title}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-neutral-500">
            <span className="font-medium text-neutral-700">{assigneeLabel(t, names)}</span><span>· {FREQ[t.frequency]}</span>
            <span className="inline-flex items-center gap-1">· <Clock className="h-3 w-3" /> {t.time}</span><span>· Prazo {fmtDate(t.dueDate)}</span>
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Tag tone={st.tone} testId={`task-status-${t.id}`}>{st.label}</Tag>
            {isOverdue(t) && <Tag tone="red" testId={`task-overdue-${t.id}`}>Atrasada</Tag>}
            <Tag tone={PRIO[t.priority]}>{t.priority}</Tag>
            {t.requiresApproval && <Tag>Requer aprovação</Tag>}
            {t.kind === "operacional" && <Tag tone="gold" testId={`task-kind-${t.id}`}>Operacional · foto</Tag>}
            {t.proof && <button onClick={() => setDlg("photo")} data-testid={`task-photo-${t.id}`} className="inline-flex items-center gap-1 rounded-full border border-gold/40 px-2.5 py-0.5 text-[11px] font-medium text-gold-dark hover:border-gold"><Camera className="h-3 w-3" /> Ver foto</button>}
            {proc && <Tag tone="gold">{proc.name}</Tag>}
          </div>
        </div>
        <TaskMenu t={t} mode={mode} open={setDlg} />
      </div>
      {mode !== "view" && <div className="mt-3"><StatusControls t={t} mode={mode} actor={actor} open={setDlg} /></div>}
      <TaskDialog open={dlg === "edit"} onOpenChange={close} initial={t} people={people} processes={processes} onSave={(x) => { saveTask({ ...x, id: t.id }, actor); toast.success("Tarefa atualizada."); setDlg(null); }} />
      <ConfirmDialog open={dlg === "delete"} onOpenChange={close} title="Excluir tarefa?" description={t.title} testId={`task-delete-dialog-${t.id}`} onConfirm={() => { deleteTask(t.id); toast.success("Tarefa excluída."); }} />
      <NoteDialog open={dlg === "reject"} onOpenChange={close} title="Devolver para correção" label="O que precisa ser corrigido?" confirmLabel="Devolver" testId={`task-reject-dialog-${t.id}`} onConfirm={(n) => { reviewTask(t.id, false, actor, n); toast.success("Tarefa devolvida."); setDlg(null); }} />
      <NoteDialog open={dlg === "reopen"} onOpenChange={close} title="Reabrir tarefa" label="Motivo da reabertura ou correção (fica registrado no histórico)" confirmLabel="Reabrir" testId={`task-reopen-dialog-${t.id}`} onConfirm={(n) => { reopenTask(t.id, actor, n); toast.success("Tarefa reaberta."); setDlg(null); }} />
      <HistoryDialog open={dlg === "history"} onOpenChange={close} title={t.title} history={t.history} testId={`task-history-dialog-${t.id}`} />
      <Dialog open={dlg === "proof"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid={`task-proof-dialog-${t.id}`}>
          <DialogHeader><DialogTitle className="font-display text-xl">Foto da atividade</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-neutral-500">{t.title}</p>
          <ProofForm requirePhoto companyId={t.companyId} testId={`task-proof-${t.id}`} onSave={(p) => { completeWithProof(t.id, p, actor); toast.success(t.requiresApproval ? "Enviada para aprovação." : "Tarefa concluída com foto."); setDlg(null); }} />
        </DialogContent>
      </Dialog>
      <Dialog open={dlg === "photo"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid={`task-photo-dialog-${t.id}`}>
          <DialogHeader><DialogTitle className="font-display text-xl">Prova da atividade</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-neutral-500">{t.title}</p>
          {t.proof && (
            <div className="space-y-3">
              {t.proof.photo && <img src={t.proof.photo} alt="Prova" className="max-h-80 w-full rounded-xl object-cover" data-testid={`task-photo-img-${t.id}`} />}
              {t.proof.note && <p className="rounded-lg bg-neutral-50 p-4 text-sm text-neutral-700">{t.proof.note}</p>}
              <p className="text-xs text-neutral-400">Enviada em {fmtDate(t.proof.date)}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </li>
  );
}

export function TaskBoard({ tasks, mode, companyId, actor, title = "Tarefas" }) {
  const { state, saveTask } = useStore();
  const [filter, setFilter] = useState("todas");
  const [freq, setFreq] = useState("todas");
  const [creating, setCreating] = useState(false);
  const people = teamOf(state, companyId);
  const processes = state.processes.filter((p) => p.companyId === companyId);
  const names = nameMap(state);
  const list = sortTasks(tasks.filter((t) => match(t, filter) && (freq === "todas" || t.frequency === freq)));
  return (
    <section data-testid="task-board" className="rounded-xl border border-neutral-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <div className="flex gap-2">
          <Select value={freq} onValueChange={setFreq}>
            <SelectTrigger className="h-9 w-36" data-testid="task-freq-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="todas">Todas frequências</SelectItem>{Object.entries(FREQ).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {mode === "owner" && <Button className="h-9 font-semibold" onClick={() => setCreating(true)} data-testid="new-task-button"><Plus className="mr-1 h-4 w-4" /> Nova tarefa</Button>}
        </div>
      </div>
      <div className="mb-2 flex gap-1.5 overflow-x-auto pb-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`task-filter-${k}`} className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === k ? "border-gold bg-gold text-neutral-900" : "border-neutral-200 text-neutral-600 hover:border-gold"}`}>
            {l} <span className="opacity-60">{tasks.filter((t) => match(t, k)).length}</span>
          </button>
        ))}
      </div>
      {!list.length && <p className="py-6 text-sm text-neutral-400" data-testid="task-board-empty">Nenhuma tarefa neste filtro.</p>}
      <ul className="divide-y divide-neutral-100">
        {list.map((t) => <TaskRow key={t.id} t={t} mode={mode} actor={actor} names={names} people={people} processes={processes} />)}
      </ul>
      <TaskDialog open={creating} onOpenChange={setCreating} people={people} processes={processes} onSave={(x) => { saveTask({ ...x, companyId }, actor); toast.success("Tarefa criada."); setCreating(false); }} />
    </section>
  );
}
